/**
 * NetStats — the client's view of its own connection quality.
 *
 * Everything the netcode tunes itself by, and everything the debug overlay
 * shows, comes from here: round-trip time (from the ping/pong the socket
 * already does), snapshot arrival jitter (how unevenly `state` packets land
 * relative to the server clock they carry), reconciliation corrections
 * (how often prediction was wrong, and by how much), the negotiated
 * transport, and the interpolation delay derived from all of it.
 *
 * Pure bookkeeping; no DOM, no PIXI, no socket. Node-testable.
 */

const RTT_WINDOW = 10;
const JITTER_WINDOW = 40;
const INTERVAL_WINDOW = 20;
const CORRECTION_WINDOW_MS = 60_000;

function percentile(sorted, p) {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor(p * (sorted.length - 1)));
  return sorted[idx];
}

export class NetStats {
  constructor({ defaultInterpolationMs = 100, maxInterpolationMs = 150 } = {}) {
    this.defaultInterpolationMs = defaultInterpolationMs;
    this.maxInterpolationMs = maxInterpolationMs;
    this.rttSamples = [];
    this.rtt = 0;
    this.jitterSamples = [];
    this.intervalSamples = [];
    this.lastSnapshot = null; // { serverTime, recvTime }
    this.corrections = [];    // { t, dist }
    this.transport = null;
  }

  onPing(rttMs) {
    if (!Number.isFinite(rttMs) || rttMs < 0) return;
    this.rttSamples.push(rttMs);
    if (this.rttSamples.length > RTT_WINDOW) this.rttSamples.shift();
    const sorted = [...this.rttSamples].sort((a, b) => a - b);
    this.rtt = sorted[Math.floor(sorted.length / 2)]; // median: one lost ping does not spike the display
  }

  /**
   * A `state` snapshot arrived. Jitter is how far the receive-time gap strayed
   * from the server-time gap the two snapshots claim; the interval is the
   * server's broadcast cadence as observed.
   */
  onSnapshot(serverTime, recvTime) {
    if (typeof serverTime !== 'number' || typeof recvTime !== 'number') return;
    const last = this.lastSnapshot;
    this.lastSnapshot = { serverTime, recvTime };
    if (!last) return;
    const serverGap = serverTime - last.serverTime;
    const recvGap = recvTime - last.recvTime;
    if (serverGap <= 0 || serverGap > 1000) return; // duplicate / resync / long stall: not a jitter sample
    this.intervalSamples.push(serverGap);
    if (this.intervalSamples.length > INTERVAL_WINDOW) this.intervalSamples.shift();
    this.jitterSamples.push(Math.abs(recvGap - serverGap));
    if (this.jitterSamples.length > JITTER_WINDOW) this.jitterSamples.shift();
  }

  onCorrection(dist, now = Date.now()) {
    this.corrections.push({ t: now, dist });
    const cutoff = now - CORRECTION_WINDOW_MS;
    while (this.corrections.length && this.corrections[0].t < cutoff) this.corrections.shift();
  }

  setTransport(name) {
    this.transport = name || null;
  }

  /** Observed broadcast interval (median), or null before two snapshots. */
  broadcastIntervalMs() {
    if (this.intervalSamples.length === 0) return null;
    const sorted = [...this.intervalSamples].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }

  jitter95Ms() {
    if (this.jitterSamples.length < 5) return 0;
    return percentile([...this.jitterSamples].sort((a, b) => a - b), 0.95);
  }

  /**
   * How far behind the server clock remote players are rendered.
   * Enough to always have two snapshots to interpolate between, plus the
   * jitter actually seen, never more than maxInterpolationMs. Before there is
   * data it is the configured default.
   */
  interpolationDelayMs() {
    const interval = this.broadcastIntervalMs();
    if (interval === null || this.jitterSamples.length < 5) return this.defaultInterpolationMs;
    const wanted = interval * 1.5 + this.jitter95Ms();
    return Math.round(Math.min(this.maxInterpolationMs, Math.max(interval * 2, wanted)));
  }

  correctionsPerMinute(now = Date.now()) {
    const cutoff = now - CORRECTION_WINDOW_MS;
    return this.corrections.filter((c) => c.t >= cutoff).length;
  }

  meanCorrectionPx(now = Date.now()) {
    const cutoff = now - CORRECTION_WINDOW_MS;
    const live = this.corrections.filter((c) => c.t >= cutoff);
    if (live.length === 0) return 0;
    return live.reduce((a, c) => a + c.dist, 0) / live.length;
  }

  summary(now = Date.now()) {
    return {
      rtt: Math.round(this.rtt),
      jitter95: Math.round(this.jitter95Ms()),
      interval: this.broadcastIntervalMs() === null ? null : Math.round(this.broadcastIntervalMs()),
      interp: this.interpolationDelayMs(),
      correctionsPerMin: this.correctionsPerMinute(now),
      meanCorrection: Math.round(this.meanCorrectionPx(now) * 10) / 10,
      transport: this.transport
    };
  }

  reset() {
    this.rttSamples = [];
    this.rtt = 0;
    this.jitterSamples = [];
    this.intervalSamples = [];
    this.lastSnapshot = null;
    this.corrections = [];
  }
}
